import { Combobox } from '@headlessui/react';
import { Transition } from '@headlessui/react';
import { CheckIcon } from '@heroicons/react/20/solid';
import { MinusIcon } from '@heroicons/react/20/solid';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import cn from 'classnames';
import React, { Fragment, useCallback, useMemo, useRef, useState } from 'react';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { Setter } from 'libs/end-to-end/src/ui-types';
import { replaceTextColor } from 'apps/sigma2-ui/src/components/ui/Typography';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { ListItem } from 'libs/end-to-end/src/types/generic/generic';
import { Box } from 'apps/sigma2-ui/src/components/ui/Box';

interface BaseProps<T extends string> {
  list: ListItem<T>[];
  label: string | null;
  widthClass?: string; // Width Class can be used to set the "height" if the select box is used in a flex-col.
  disabled?: boolean;
  enableQuery?: boolean;
  left?: (value: T | null) => React.ReactNode;
}

interface SingleProps<T extends string> extends BaseProps<T> {
  type: 'single';
  setSelected: Setter<T>;
  selected: T;
}

interface MultiProps<T extends string> extends BaseProps<T> {
  type: 'multi';
  setSelected: Setter<T[]>;
  selected: T[];
}

interface SingleWithAllProps<T extends string> extends BaseProps<T> {
  type: 'single-with-all';
  setSelected: Setter<T[]>;
  selected: T[];
}

const textProps = 'text-gray-1000';

const backgroundProps = (disabled: boolean) => {
  if (disabled) {
    return 'bg-gray-200';
  }
  return 'bg-white group-hover:bg-blue-100 hover:bg-blue-100';
};

const checkboxClasses =
  'items-center mr-2 min-h-[24px] min-w-[24px] border-2 rounded-md text-white';

export const SelectBoxWithSearch = <T extends string>(
  props: SingleProps<T> | MultiProps<T> | SingleWithAllProps<T>,
) => {
  const { list, label, setSelected, selected, type, widthClass, disabled } =
    props;
  const enableQuery = props.enableQuery ?? true;
  const leftComponent = props.left ?? (() => null);
  const selectAllId = 'ReservedSelectAll';
  const [query, setQuery] = useState('');
  const comboboxButtonRef = useRef<HTMLButtonElement>(null);
  const language = useLanguage();

  const checkboxProps = {
    borderColor: 'border-[#323232]',
    selectedBackgroundColor: 'bg-[#323232]',
  };

  // Check if the dropdown is open.
  const isOpen = useCallback(() => {
    return comboboxButtonRef.current?.getAttribute('aria-expanded') === 'true';
  }, []);

  // Opens the dropdown if it is closed, closes it if it is open.
  const toggle = useCallback(() => {
    comboboxButtonRef.current?.click();
  }, []);

  // Opens the dropdown if it is closed.
  const forceOpen = useCallback(() => {
    if (!isOpen()) {
      toggle();
    }
  }, [isOpen, toggle]);

  // Closes the dropdown if it is open.
  const forceClose = useCallback(() => {
    if (isOpen()) {
      toggle();
    }
  }, [isOpen, toggle]);

  const filtered = useMemo(() => {
    return query === '' || !enableQuery
      ? list
      : list.filter((item) =>
          item.optionLabel
            .toLowerCase()
            .replace(/\s+/g, '')
            .includes(query.toLowerCase().replace(/\s+/g, '')),
        );
  }, [list, query, enableQuery]);

  const selectedArray = useMemo(() => {
    return type === 'single'
      ? [...(selected ? [selected] : [])]
      : [...selected];
  }, [type, selected]);

  const selectedItems = useMemo(() => {
    return list.filter((item) => selectedArray.includes(item.optionValue));
  }, [list, selectedArray]);

  const allAreSelected = useMemo(() => {
    return selectedItems.length === list.length;
  }, [selectedItems.length, list.length]);

  const handleSelectAll = useCallback(() => {
    if (type === 'single') {
      return;
    }

    const missingFilteredItems = filtered.filter(
      (item) => !selectedArray.includes(item.optionValue),
    );

    if (missingFilteredItems.length > 0) {
      const selectedPlusMissing = selectedArray.concat(
        missingFilteredItems.map((item) => item.optionValue),
      );
      setSelected(selectedPlusMissing);
    } else {
      const selectedMinusFiltered = selectedArray.filter(
        (item) => !filtered.map((item) => item.optionValue).includes(item),
      );
      setSelected(selectedMinusFiltered);
    }
  }, [filtered, selectedArray, setSelected, type]);

  const allFilteredItemsAreSelected = useMemo(() => {
    return filtered.every((item) => selectedArray.includes(item.optionValue));
  }, [filtered, selectedArray]);

  const selectionDisplay = useCallback(
    ({ selectedItems }: { selectedItems: ListItem<T>[] }) => {
      if (selectedItems.length === 0) {
        return label ?? 'N/A';
      }

      if (selectedItems.length === 1) {
        return selectedItems[0].optionLabel;
      }

      if (selectedItems.length === 2) {
        return selectedItems.map((item) => item.optionLabel).join(', ');
      }

      if (selectedItems.length === list.length) {
        return language.selectAllLabel({
          count: selectedItems.length,
        });
      }

      return `${selectedItems[0].optionLabel}, ${
        selectedItems[1].optionLabel
      } (+${selectedItems.length - 2})`;
    },
    [label, language, list.length],
  );

  const contents = useCallback(
    ({ open, disabled }: { open: boolean; disabled: boolean }) => {
      return (
        <div
          className={cn(
            'relative text-base border-b-2 border-transparent',
            open && 'border-secondary',
            !open && !disabled && 'cursor-pointer',
          )}
          onClick={() => {
            if (!open) forceOpen();
          }}
        >
          <div
            className={cn(
              `relative flex flex-col w-full overflow-hidden px-2 pt-2 pb-1.5 rounded text-left border-1 border-gray-200`,
              'group',
              backgroundProps(disabled),
            )}
          >
            {label && (
              <S2Typography
                dangerouslyOverrideClassNameFn={replaceTextColor(
                  'text-gray-700',
                )}
                variant="small-label"
                translatedContent={language.dangerousUserInput({
                  value: label ?? '',
                })}
              />
            )}
            <div className={cn('flex gap-1', backgroundProps(disabled))}>
              {leftComponent(selectedItems[0]?.optionValue ?? null)}
              {enableQuery && (
                <Combobox.Input
                  className={cn(
                    'w-full border-none p-0 text-sm leading-6 font-medium',
                    backgroundProps(disabled),
                    textProps,
                    !open && !disabled && 'cursor-pointer',
                  )}
                  displayValue={() => {
                    return selectionDisplay({ selectedItems });
                  }}
                  onClick={() => {
                    if (!open) forceOpen();
                  }}
                  defaultValue={query}
                  onChange={(event) => setQuery(event.target.value)}
                />
              )}
              {!enableQuery && (
                <Combobox.Label>
                  <S2Typography
                    dangerouslyOverrideClassNameFn={replaceTextColor(
                      'text-gray-1000',
                    )}
                    variant="headline-7-alt"
                    translatedContent={selectionDisplay({
                      selectedItems,
                    })}
                  />
                </Combobox.Label>
              )}
              <Combobox.Button
                className="right-0 flex items-center"
                ref={comboboxButtonRef}
              >
                <ChevronDownIcon
                  className="h-5 w-5 text-gray-700"
                  aria-hidden="true"
                />
              </Combobox.Button>
            </div>
          </div>
          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            afterLeave={() => setQuery('')}
          >
            <Combobox.Options
              className={cn(
                `absolute z-tooltip mt-1 max-h-40 min-w-full overflow-auto rounded-md py-1 text-base shadow-8px ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm bg-white`,
              )}
            >
              {filtered.length === 0 && query !== '' ? (
                <div
                  className={cn(
                    'relative cursor-default select-none py-2 px-4',
                    textProps,
                  )}
                >
                  {language.noResultsFound()}
                </div>
              ) : (
                <>
                  {(type === 'multi' || type === 'single-with-all') && (
                    <Combobox.Option
                      key={selectAllId}
                      value={selectAllId}
                      onClick={(e) => {
                        if (type === 'multi') {
                          e.preventDefault();
                          handleSelectAll();
                        }
                      }}
                    >
                      {({ selected: isSelected }) => (
                        <DropdownItemSingle
                          isSelected={false}
                          isHighlighted={
                            type === 'single-with-all' ? allAreSelected : false
                          }
                          isSelectAllItem={true}
                        >
                          {type === 'multi' && (
                            <span
                              className={cn(
                                checkboxClasses,
                                checkboxProps.borderColor,
                                allFilteredItemsAreSelected &&
                                  checkboxProps.selectedBackgroundColor,
                              )}
                            >
                              {allFilteredItemsAreSelected && (
                                <MinusIcon
                                  className="h-5 w-5"
                                  aria-hidden="true"
                                />
                              )}
                            </span>
                          )}
                          <span
                            className={cn(
                              'block truncate',
                              isSelected ? 'font-medium' : 'font-normal',
                            )}
                          >
                            {type === 'single-with-all'
                              ? language.selectAllLabel({ count: list.length })
                              : language.selectAll()}
                          </span>
                        </DropdownItemSingle>
                      )}
                    </Combobox.Option>
                  )}
                  {filtered.map((item) => (
                    <Combobox.Option
                      key={item.optionValue}
                      value={item.optionValue}
                    >
                      {({ selected: isSelected }) => (
                        <DropdownItemSingle
                          isHighlighted={
                            type === 'multi'
                              ? false
                              : type === 'single-with-all'
                                ? !allAreSelected && isSelected
                                : isSelected
                          }
                          isSelected={isSelected}
                        >
                          <>
                            {type === 'multi' && (
                              <span
                                className={cn(
                                  checkboxClasses,
                                  checkboxProps.borderColor,
                                  isSelected &&
                                    checkboxProps.selectedBackgroundColor,
                                )}
                              >
                                {isSelected && (
                                  <CheckIcon
                                    className="h-5 w-5"
                                    aria-hidden="true"
                                  />
                                )}
                              </span>
                            )}
                            <span
                              className={cn(
                                'block truncate',
                                type === 'single-with-all'
                                  ? 'font-normal'
                                  : isSelected
                                    ? 'font-medium'
                                    : 'font-normal',
                              )}
                            >
                              {item.optionLabel}
                            </span>
                          </>
                        </DropdownItemSingle>
                      )}
                    </Combobox.Option>
                  ))}
                </>
              )}
            </Combobox.Options>
          </Transition>
        </div>
      );
    },
    [
      allAreSelected,
      allFilteredItemsAreSelected,
      checkboxProps.borderColor,
      checkboxProps.selectedBackgroundColor,
      filtered,
      forceOpen,
      handleSelectAll,
      label,
      language,
      list.length,
      query,
      selectedItems,
      type,
      selectionDisplay,
      enableQuery,
      leftComponent,
    ],
  );

  return (
    <div
      className={`${widthClass ?? 'w-full max-w-48'} font-sans`}
      data-testid={`dropdown-${label}`}
      data-dropdown-type={type}
    >
      {type === 'single' && (
        <Combobox
          value={selected === null ? [] : [selected]}
          onChange={(a) => {
            try {
              if (selected && !a.includes(selected)) {
                // Since this is a single select, we actually do not de-select.
                return;
              }

              const firstNonSelectedItem = a.find((item) => item !== selected);
              if (!firstNonSelectedItem) {
                return;
              }

              setSelected(firstNonSelectedItem);
            } finally {
              forceClose();
            }
          }}
          multiple={true}
          disabled={disabled}
        >
          {contents}
        </Combobox>
      )}
      {(type === 'multi' || type === 'single-with-all') && (
        <Combobox
          value={selected}
          onChange={(a) => {
            if (type === 'multi') {
              setSelected(a);
              return;
            }

            try {
              const itemAdded =
                a.length > selected.length
                  ? a.find((item) => !selected.includes(item))
                  : undefined;

              const itemRemoved =
                a.length < selected.length
                  ? selected.find((item) => !a.includes(item))
                  : undefined;

              const itemClicked = itemAdded ?? itemRemoved;

              if (itemClicked === undefined) {
                return;
              }

              if (itemClicked === selectAllId) {
                setSelected(list.map((item) => item.optionValue));
              } else {
                setSelected([itemClicked]);
              }
            } finally {
              forceClose();
            }
          }}
          multiple={true}
          disabled={disabled}
        >
          {contents}
        </Combobox>
      )}
    </div>
  );
};

export const DropdownItemSingle = ({
  isHighlighted,
  children,
  isSelected,
  isSelectAllItem,
}: {
  isHighlighted: boolean;
  children: React.ReactNode;
  isSelected: boolean;
  isSelectAllItem?: boolean;
}) => {
  return (
    <div
      className={cn(
        'relative flex items-center cursor-default select-none p-2 border-l-2',
        textProps,
        !isHighlighted &&
          'hover:bg-blue-100 border-transparent hover:border-gray-300',
        isHighlighted && 'bg-highlight border-gray-300',
      )}
      data-is-selected={isSelected ? 'true' : 'false'}
      data-is-select-all-item={isSelectAllItem ? 'true' : 'false'}
    >
      {children}
    </div>
  );
};
